<template>
  <div>
    <div class="wrapper">
      <breadcrumb :crumbs="[$t('menu.deliveries'), $t('text.list')]">
      </breadcrumb>
    </div>
    <narrow
      :narrows="narrows"
      :init-filter="initFilter"
      @narrowSelect="narrowSelect"
    />
    <div class="search with-narrows">
      <el-form
        :inline="true"
        @submit.native.prevent="search"
        :model="searchForm"
        size="small"
      >
        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.keyword"
            :placeholder="$t('placeholder.serialNoTracking')"
          />
        </el-form-item>

        <el-form-item>
          <el-input
            v-model.trim="searchForm.express"
            :placeholder="$t('delivery.expressSender')"
            size="small"
          />
        </el-form-item>
        <el-form-item>
          <el-input
            v-model.trim="searchForm.customerName"
            :placeholder="$t('menu.customers')"
            size="small"
          />
        </el-form-item>

        <el-form-item>
          <el-input
            size="small"
            v-model.trim="searchForm.productNo"
            :placeholder="$t('product.productNo')"
          />
        </el-form-item>

        <date-range
          v-model="searchForm"
          type="datetime"
          field="deliverAt"
          :start-placeholder="$t('rules.startAt', [$t('delivery.deliveryAt')])"
          :end-placeholder="$t('rules.endAt', [$t('delivery.deliveryAt')])"
        />
        <el-form-item>
          <el-button
            type="primary"
            icon="el-icon-search"
            native-type="submit"
          />
        </el-form-item>
      </el-form>
    </div>

    <div class="wrapper table-area">
      <sticky
        :sticky-top="62"
        :viewport="1024"
        :is-rem="false"
        style="min-height: 52px"
      >
        <vxe-toolbar ref="recordsToolbar" custom zoom class="table-toolbar">
          <template v-slot:buttons>
            <span class="pager-total">
              {{ $t("rules.pagerTotal", [pager.total]) }}
            </span>
          </template>
        </vxe-toolbar>
      </sticky>
      <vxe-table
        :data="records"
        @sort-change="sortRecords"
        :sort-config="{ remote: true }"
        :loading="loading"
        id="deliveryTable"
        :span-method="mergeMethod"
        :row-class-name="calculateBgColor"
        ref="recordsTable"
        class="recordsTable"
        size="mini"
        resizable
        auto-resize
        highlight-hover-row
        :scroll-y="{ enabled: false }"
      >
        <vxe-table-column field="type" :title="$t('text.type')" width="94">
          <template v-slot="{ row }">
            <span v-if="!row.isMergeLine">
              <span v-if="row.entity != null">
                <div :class="'tag-type tag-type-' + row.entity.type">
                  {{ $t("itemTypes." + row.entity.type) }}
                </div>
              </span>
            </span>
            <div v-else>
              <span class="mgr5">
                {{ row.serialNo }}
              </span>
              <span class="mgr5">
                {{ $t("deliveryTypes." + row.deliverType) }}
              </span>

              <span v-if="row.deliverType == 'carry'" class="font10 mgr5">
                ({{ row.sender }})
              </span>
              <span v-if="row.deliverType == 'express'" class="font10 mgr5">
                {{ row.express }}
                <span v-if="row.tracking != null"> ({{ row.tracking }} ) </span>
              </span>
              <span
                v-if="row.deliverCost != null && row.deliverCost != 0"
                class="font10 mgr5"
              >
                {{ $t("delivery.deliverCost") }}:{{ row.deliverCost }}
              </span>

              <span class="float-right font10">
                {{ $t("menu.customers") }}: {{ row.customerName }}({{
                  row.customerPhone
                }})
                <span
                  v-if="
                    row.customerProvince != null ||
                    row.customerCity != null ||
                    row.customerAddress != null
                  "
                  >{{ $t("tenant.address") }}: {{ row.customerProvince
                  }}{{ row.customerCity }}{{ row.customerAddress }}
                </span>
                <span class="mgl5">
                  {{ row.deliverName }} {{ row.deliverAt }}
                </span>
              </span>
            </div>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="images"
          :title="$t('product.images')"
          width="80"
        >
          <template v-slot="{ row }">
            <span v-if="row.entity != null">
              <thumbnail :images="row.entity.images"></thumbnail>
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="serialNo"
          :title="$t('text.serialNo')"
          min-width="140"
        >
          <template v-slot="{ row }">
            <span v-if="row.entity != null">
              <small v-if="row.entity.hurry" class="red">
                ({{ $t("order.hurrySimple") }})
              </small>
              {{ row.entity.serialNo }}
              <div>
                <state-label
                  :state="row.entity.workflowState"
                  v-if="['rent', 'clean', 'service'].includes(row.entity.type)"
                >
                  {{ $t("rentStates." + row.entity.workflowState) }}
                </state-label>
                <state-label :state="row.entity.workflowState" v-else>
                  {{ $t("orderStates." + row.entity.workflowState) }}
                </state-label>
              </div>
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="productNo"
          :title="$t('product.productNo')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <span v-if="row.entity != null">
              {{ row.entity.productNo }}
              <div class="font10">
                {{ row.entity.categoryName
                }}<span v-if="row.entity.brand !== null">/</span
                >{{ row.entity.brand }}
              </div>
            </span>
            <!-- <div class="font10">{{ row.color }} {{ row.size }}</div> -->
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="color"
          :title="$t('product.color')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <span v-if="row.entity != null">
              {{ row.entity.color }}
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="size"
          :title="$t('product.size')"
          min-width="100"
        >
          <template v-slot="{ row }">
            <span v-if="row.entity != null">
              {{ row.entity.color }}
            </span>
          </template>
        </vxe-table-column>
        <vxe-table-column
          field="quantity"
          :title="$t('text.quantity')"
          min-width="50"
        >
          <template v-slot="{ row }">
            <span v-if="row.entity != null">
              {{ row.entity.quantity }}{{ row.entity.unit || "" }}
            </span>
          </template>
        </vxe-table-column>

        <vxe-table-column
          field="remark"
          :title="$t('text.remark')"
          min-width="140"
        >
          <template v-slot="{ row }">
            <raw-html-limit-number
              v-if="row.entity != null"
              :content="row.entity.remark"
              :number="24"
              type="richText"
            />
          </template>
        </vxe-table-column>
      </vxe-table>
      <div class="flex flex-between pager">
        <div class="selected-qty-tips"></div>
        <div>
          <vxe-pager
            size="small"
            :loading="false"
            :background="false"
            :current-page="pager.currentPage"
            :page-size="pager.pageSize"
            :total="pager.total"
            :layouts="pagerConfig.layouts"
            :page-sizes="pagerConfig.pageSizes"
            @page-change="changePager"
          ></vxe-pager>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { table, page } from "@/utils/mixins.js";
import Util from "@/utils";
import RawHtml from "@/components/RawHtml";
import DateRange from "@/components/DateRange";
import Delivery from "@/api/delivery.js";

export default {
  mixins: [
    page,
    table.pager,
    table.narrow,
    table.search,
    table.checker,
    table.sorter,
    table.basic,
  ],
  components: {
    RawHtml,
    DateRange,
  },
  data() {
    return {
      Util: Util,
      searchForm: {
        keyword: null,
        express: null,
        productNo: null,
        customerName: null,
        deliverAtStartAt: null,
        deliverAtEndAt: null,
      },
      sorter: {
        sortBy: "delivery_id",
        sortIndicator: "desc",
      },
      sorterMap: {
        productNo: "delivery_productNo",
      },
      narrows: [
        [
          {
            label: this.$t("delivery.deliverType"),
            items: Delivery.deliverTypes.map((s) => {
              return {
                key: s,
                label: this.$t("deliveryTypes." + s),
                condition: { deliverType: s },
              };
            }),
          },
        ],
      ],
    };
  },

  created() {
    const self = this;
  },

  methods: {
    //
    dataProvider(params) {
      return Delivery.search(params);
    },
    recordsDecorate(records) {
      this.records = this.buildTableData(records);
      this.mergeCells = this.buildMergeCells(records);
    },
    buildTableData(records) {
      let rs = [];
      records.forEach((record) => {
        let bOrder = {
          isMergeLine: true,
          id: record.id,
          serialNo: record.serialNo,
          deliverId: record.deliverId,
          deliverName: record.deliverName,
          deliverAt: record.deliverAt,
          deliverCost: record.deliverCost,
          deliverType: record.deliverType,
          express: record.express,
          tracking: record.tracking,
          weight: record.tracking,
          customerName: record.customerName,
          customerPhone: record.customerPhone,
          customerProvince: record.customerProvince,
          customerCity: record.customerCity,
          customerAddress: record.customerAddress,
          deliveryItems: record.deliveryItems,
        };
        rs.push(bOrder); //将bargain的数据转换到格式，放到数据列表
        record.deliveryItems.forEach((item) => {
          rs.push(item);
        });
      });
      return rs;
    },
    buildMergeCells(records) {
      let mcs = [];
      for (let i = 0, rowIdx = 0; i < records.length; i++) {
        mcs.push({ row: rowIdx, col: 0, rowspan: 1, colspan: 7 });
        rowIdx = rowIdx + records[i].deliveryItems.length + 1;
      }
      return mcs;
    },
    calculateBgColor({ row, rowIndex }) {
      return row.isMergeLine ? "light-blue-bg" : "";
    },
  },
};
</script>

<style scoped>
</style>